import { useEffect, useState } from 'react';
import { useNow } from '../../../data/kioskRefresh';

// Kitchen kiosk clock widget. Split left/right:
//   - Left: flip-clock style time. Two flap panels (HH, MM) with a real
//     split-flap animation — top half falls forward, then bottom half
//     rises into place. Black midline seam at the hinge axis. Date and
//     AM/PM are small uppercase tracked stamps above and below.
//   - Right: vertical 7-day weekday strip centered on today, with today
//     enlarged and brightened so the current day reads at a glance.
//
// 12-hour format, minute precision (no seconds). Ticks at 1Hz so the
// minute rolls over promptly without interval drift.
//
// The flip-clock block is also exported as <FlipClock /> so the kiosk
// lock screen can reuse it at a larger size.

// Total duration of a flip: top half falls (TOP_MS), then bottom half
// rises (BOT_MS). Keep these in sync with the inline `animation` strings
// below — the `setTimeout` that swaps `prev → current` fires at the end
// of the second phase.
const FLIP_TOP_MS = 300;
const FLIP_BOT_MS = 300;
const FLIP_TOTAL_MS = FLIP_TOP_MS + FLIP_BOT_MS;

type FlipSize = 'default' | 'large';

interface FlipSizeClasses {
  panel: string;
  panelRadius: string;
  digit: string;
  colonDot: string;
  colonGap: string;
  panelsGap: string;
  stamp: string;
  blockGap: string;
}

const SIZE_CLASSES: Record<FlipSize, FlipSizeClasses> = {
  default: {
    panel: 'w-23 h-23',
    panelRadius: 'rounded-2xl',
    digit: 'text-6xl',
    colonDot: 'w-2 h-2',
    colonGap: 'gap-3',
    panelsGap: 'gap-3',
    stamp: 'text-xs',
    blockGap: 'gap-2',
  },
  large: {
    panel: 'w-72 h-72',
    panelRadius: 'rounded-[2.5rem]',
    digit: 'text-[12rem]',
    colonDot: 'w-4 h-4',
    colonGap: 'gap-6',
    panelsGap: 'gap-6',
    stamp: 'text-base',
    blockGap: 'gap-4',
  },
};

// Renders the digit inside a 200%-height container, positioned so that
// when clipped by a half-height parent, the digit appears centered in the
// FULL panel. Top half pins to the parent's top edge (digit center lands
// at parent's bottom = panel midline); bottom half pins to the bottom
// (digit center lands at parent's top = panel midline). Either way the
// numeral sits visually centered on the seam.
function DigitInHalf({
  digits,
  half,
  digitClass,
}: {
  digits: string;
  half: 'top' | 'bottom';
  digitClass: string;
}) {
  return (
    <div
      className={`absolute left-0 right-0 h-[200%] flex items-center justify-center ${
        half === 'top' ? 'top-0' : 'bottom-0'
      }`}
    >
      <span className={`${digitClass} font-semibold tabular leading-none`}>
        {digits}
      </span>
    </div>
  );
}

// The 3D sheen, filling one flap half and layered OVER the digit, so the
// lighting is "printed" on the card and its number and rotates with the flap
// instead of staying behind a fixed pane of glass. Lit from above: each half
// is bright at its top and darkens toward its own bottom (see flip-sheen).
function SheenInHalf() {
  return <div className="flip-sheen absolute inset-0 pointer-events-none" />;
}

// Split-flap panel with a real flip animation. Four stacked layers when
// the digit changes:
//   1. Static top    — shows the NEW digit's top half (revealed as flap falls)
//   2. Static bottom — shows the OLD digit's bottom half (covered by rising flap)
//   3. Falling flap  — OLD digit's top half, rotates 0 → -90 around bottom edge
//   4. Rising flap   — NEW digit's bottom half, rotates 90 → 0 around top edge
// After FLIP_TOTAL_MS we sync `prev` to the current digit, the flipping
// layers unmount, and the panel returns to a clean two-layer steady state.
function FlipPanel({
  digits,
  panelClass,
  panelRadiusClass,
  digitClass,
}: {
  digits: string;
  panelClass: string;
  panelRadiusClass: string;
  digitClass: string;
}) {
  // `prev` lags behind `digits` during the animation so static-bottom keeps
  // showing the OLD digit until the rising flap covers it.
  const [prev, setPrev] = useState(digits);
  // Bumped on every flip so React re-mounts the animating divs (via `key`)
  // and the CSS animations re-trigger from their `from` state.
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (digits !== prev) {
      setAnimKey((k) => k + 1);
      const t = setTimeout(() => setPrev(digits), FLIP_TOTAL_MS);
      return () => clearTimeout(t);
    }
  }, [digits, prev]);

  const animating = digits !== prev;

  return (
    <div
      className={`relative ${panelClass} ${panelRadiusClass} bg-subtle border border-border overflow-hidden`}
      style={{ perspective: '400px' }}
    >
      <div className="absolute left-0 right-0 top-0 h-1/2 overflow-hidden">
        <DigitInHalf digits={digits} half="top" digitClass={digitClass} />
        <SheenInHalf />
      </div>
      <div className="absolute left-0 right-0 bottom-0 h-1/2 overflow-hidden">
        <DigitInHalf digits={prev} half="bottom" digitClass={digitClass} />
        <SheenInHalf />
      </div>

      {animating && (
        <>
          <div
            key={`top-${animKey}`}
            className="absolute left-0 right-0 top-0 h-1/2 overflow-hidden bg-subtle"
            style={{
              transformOrigin: 'bottom',
              animation: `flip-top-down ${FLIP_TOP_MS}ms ease-in both`,
              backfaceVisibility: 'hidden',
            }}
          >
            <DigitInHalf digits={prev} half="top" digitClass={digitClass} />
            <SheenInHalf />
          </div>
          <div
            key={`bot-${animKey}`}
            className="absolute left-0 right-0 bottom-0 h-1/2 overflow-hidden bg-subtle"
            style={{
              transformOrigin: 'top',
              animation: `flip-bottom-up ${FLIP_BOT_MS}ms ease-out ${FLIP_TOP_MS}ms both`,
              backfaceVisibility: 'hidden',
            }}
          >
            <DigitInHalf digits={digits} half="bottom" digitClass={digitClass} />
            <SheenInHalf />
          </div>
        </>
      )}

      <div className="absolute left-0 right-0 top-1/2 h-px bg-canvas -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

function ColonDots({
  dotClass,
  gapClass,
}: {
  dotClass: string;
  gapClass: string;
}) {
  return (
    <div className={`flex flex-col justify-center ${gapClass}`}>
      <div className={`${dotClass} rounded-full bg-fg-secondary`} />
      <div className={`${dotClass} rounded-full bg-fg-secondary`} />
    </div>
  );
}

interface FlipClockProps {
  size?: FlipSize;
  className?: string;
}

// Time + date + AM/PM block, no surrounding card. Used by ClockWidget on
// the dashboard at the default size, and by the kiosk lock screen at the
// large size.
export function FlipClock({ size = 'default', className }: FlipClockProps = {}) {
  const now = useNow(1000);
  const s = SIZE_CLASSES[size];

  const hours24 = now.getHours();
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  const hh = String(hours12).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const period = hours24 >= 12 ? 'PM' : 'AM';

  const monthDay = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className={`flex flex-col ${s.blockGap} ${className ?? ''}`}>
      <div className={`${s.stamp} uppercase tracking-widest text-fg-secondary`}>
        {monthDay}
      </div>
      <div className={`flex items-center ${s.panelsGap}`}>
        <FlipPanel digits={hh} panelClass={s.panel} panelRadiusClass={s.panelRadius} digitClass={s.digit} />
        <ColonDots dotClass={s.colonDot} gapClass={s.colonGap} />
        <FlipPanel digits={mm} panelClass={s.panel} panelRadiusClass={s.panelRadius} digitClass={s.digit} />
      </div>
      <div
        className={`${s.stamp} uppercase tracking-widest text-fg-secondary text-right`}
      >
        {period}
      </div>
    </div>
  );
}

interface DayCell {
  iso: string;
  weekdayShort: string;
  isToday: boolean;
}

function buildDayStrip(now: Date): DayCell[] {
  const out: DayCell[] = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    out.push({
      iso: d.toDateString(),
      weekdayShort: d.toLocaleDateString('en-US', { weekday: 'short' }),
      isToday: i === 0,
    });
  }
  return out;
}

interface ClockWidgetProps {
  className?: string;
}

export function ClockWidget({ className }: ClockWidgetProps = {}) {
  const now = useNow(1000);
  const days = buildDayStrip(now);

  return (
    <div className={`card flex items-center gap-6 ${className ?? ''}`}>
      <FlipClock />
      <div className="ml-auto flex flex-col items-center gap-1">
        {days.map((d, i) => {
          // Symmetric fade: today (middle, index 3) stays at full opacity,
          // each step outward drops opacity linearly so the far-top and
          // far-bottom cells feather out to the canvas.
          const distance = Math.abs(i - 3);
          const opacity = 1 - (distance / 3) * 0.8;
          return (
            <span
              key={d.iso}
              style={{ opacity }}
              className={
                d.isToday
                  ? 'text-lg font-semibold text-fg leading-tight'
                  : 'text-sm text-fg-tertiary leading-tight'
              }
            >
              {d.weekdayShort}
            </span>
          );
        })}
      </div>
    </div>
  );
}
